/**
 * OHLC bar helpers — typed core for Bharat Charts.
 */
import type { BcBar } from "./globals";
import { IST_OFFSET_MS, isMcxSymbol, sessionOpenMin, resMinutes } from "./session";

/**
 * Prefer real trade volume. When missing (NSE indices often report 0), use a
 * range-based activity proxy so the Volume study still draws histogram bars.
 */
export function displayVolume(b: Partial<BcBar> | null | undefined): number {
  const raw = Number(b && b.volume);
  if (raw > 0 && isFinite(raw)) return Math.round(raw);
  const o = Number(b && b.open) || 0;
  const h = Number(b && b.high) || 0;
  const l = Number(b && b.low) || 0;
  const c = Number(b && b.close) || 0;
  const range = Math.max(h - l, Math.abs(c - o), 0);
  if (range > 0) return Math.max(1, Math.round(range * 100));
  return 1;
}

export function toTvBars(bars: Array<Partial<BcBar> | null | undefined> | null | undefined): BcBar[] {
  const out: BcBar[] = [];
  for (let i = 0; i < (bars || []).length; i++) {
    const b = bars![i];
    if (!b) continue;
    let t = Number(b.time);
    if (!(t > 0)) continue;
    if (t < 1e12) t = t * 1000;
    const c = Number(b.close);
    if (!(c > 0)) continue;
    const bar: BcBar = {
      time: t,
      open: Number(b.open) || c,
      high: Number(b.high) || c,
      low: Number(b.low) || c,
      close: c,
      volume: Number(b.volume) || 0,
    };
    bar.volume = displayVolume(bar);
    out.push(bar);
  }
  out.sort((a, b) => a.time - b.time);
  return out;
}

/** O=H=L=C placeholder (lag pad or LTP seed). A real candle has a high/low range. */
export function barIsFlatPlaceholder(b: Partial<BcBar> | null | undefined): boolean {
  if (!b) return false;
  const o = Number(b.open);
  const h = Number(b.high);
  const l = Number(b.low);
  const c = Number(b.close);
  return c > 0 && o === h && h === l && l === c;
}

export function mergeBars(
  existing: BcBar[] | null | undefined,
  incoming: BcBar[] | null | undefined
): BcBar[] {
  const byT: Record<string, BcBar> = {};
  function add(list: BcBar[] | null | undefined) {
    for (let i = 0; i < (list || []).length; i++) {
      const b = list![i];
      if (!b || !(Number(b.time) > 0)) continue;
      const prev = byT[String(b.time)];
      // A one-price seed must not erase a candle that already has a high and low.
      // A ranged bar replaces that seed, which is what a full reload used to do.
      if (prev && !barIsFlatPlaceholder(prev) && barIsFlatPlaceholder(b)) continue;
      byT[String(b.time)] = b;
    }
  }
  add(existing);
  add(incoming);
  return Object.keys(byT)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((t) => byT[String(t)]);
}

export function aggregateTvBars(
  bars: BcBar[] | null | undefined,
  toMinutes: number,
  symbol: string
): BcBar[] {
  if (!bars || !bars.length) return bars || [];
  if (toMinutes <= 1 || toMinutes === 5) return bars;
  const openMin = sessionOpenMin(symbol);
  const out: BcBar[] = [];
  let cur: BcBar | null = null;
  let curKey: number | null = null;
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b.time);
    if (!(t > 0)) continue;
    const shifted = new Date(t + IST_OFFSET_MS);
    const mins = shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
    const offset = Math.max(0, mins - openMin);
    const aligned = openMin + Math.floor(offset / toMinutes) * toMinutes;
    const h = Math.floor(aligned / 60);
    const m = aligned % 60;
    const bucket =
      Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), h, m, 0) -
      IST_OFFSET_MS;
    if (curKey !== bucket) {
      if (cur) out.push(cur);
      cur = {
        time: bucket,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
        volume: Number(b.volume) || 0,
      };
      curKey = bucket;
    } else if (cur) {
      cur.high = Math.max(cur.high, b.high);
      cur.low = Math.min(cur.low, b.low);
      cur.close = b.close;
      cur.volume = (cur.volume || 0) + (Number(b.volume) || 0);
    }
  }
  if (cur) out.push(cur);
  for (let j = 0; j < out.length; j++) {
    out[j].volume = displayVolume(out[j]);
  }
  return out;
}

export function sanitizeTipBars(
  bars: BcBar[] | null | undefined,
  ltpHint: number,
  symbol?: string,
  dayOpenHint?: number,
  prevCloseHint?: number,
  dayHighHint?: number,
  dayLowHint?: number
): BcBar[] {
  if (!bars || !bars.length) return bars || [];
  let list = bars.slice();
  const dayOpen = Number(dayOpenHint);
  const prevClose = Number(prevCloseHint);
  if (dayOpen > 0) {
    list = repairSessionOpenBar(
      list,
      symbol || "",
      dayOpen,
      prevClose,
      Number(dayHighHint) || 0,
      Number(dayLowHint) || 0
    );
  }
  let ref = Number(ltpHint);
  if (!(ref > 0)) {
    ref = Number(list[list.length - 1].close);
  }
  if (!(ref > 0)) return list;
  const sym = String(symbol || "");
  const mcx = isMcxSymbol(sym);
  const isOpt =
    sym.indexOf("OPT:") === 0 || /\b(CE|PE)\b/i.test(sym) || /\d{4,}\s+(CE|PE)\b/i.test(sym);
  // Options: day range often >> 6% of LTP — do not drop the only tip bars.
  if (isOpt) {
    return list;
  }
  const lo = ref * (mcx ? 0.82 : 0.5);
  const hi = ref * (mcx ? 1.18 : 1.8);
  let out: BcBar[] = [];
  for (let i = 0; i < list.length; i++) {
    let b = list[i];
    const c = Number(b.close);
    const h = Number(b.high);
    const l = Number(b.low);
    const o = Number(b.open);
    if (!(c > 0)) continue;
    if (c < lo || c > hi) continue;
    if (o > 0 && (o < lo || o > hi)) continue;
    if (h > 0 && (h < lo || h > hi)) continue;
    if (l > 0 && (l < lo || l > hi)) continue;
    const maxSwing = Math.max(ref * (mcx ? 0.08 : 0.04), mcx ? 120 : 80);
    if (h > 0 && h - c > maxSwing) {
      b = Object.assign({}, b, { high: Math.max(c, o > 0 ? o : c) });
    }
    if (l > 0 && c - l > maxSwing) {
      b = Object.assign({}, b, { low: Math.min(c, o > 0 ? o : c) });
    }
    // Drop bars whose range is still absurd after clamp (bad merge / wrong sid).
    if (Number(b.high) - Number(b.low) > ref * (mcx ? 0.15 : 0.06)) continue;
    out.push(b);
  }
  if (mcx && out.length > 20) {
    const ref2 = Number(out[out.length - 1].close);
    const kept: BcBar[] = [];
    for (let j = out.length - 1; j >= 0; j--) {
      const cj = Number(out[j].close);
      if (cj < ref2 * 0.82 || cj > ref2 * 1.18) break;
      kept.push(out[j]);
    }
    if (kept.length >= 20) out = kept.reverse();
  }
  return out.length ? out : list;
}

/** Fix today's session-open bar: open = day open (not prev_close), clamp long wicks. */
export function repairSessionOpenBar(
  bars: BcBar[],
  symbol: string,
  dayOpen: number,
  prevClose: number,
  dayHigh?: number,
  dayLow?: number
): BcBar[] {
  if (!bars.length || !(dayOpen > 0)) return bars;
  // Option bars are broker candles; a 30% first-bar move is normal there, not a bad print.
  if (/^OPT:/i.test(symbol || "") || /\b(CE|PE)\b/i.test(symbol || "")) return bars;
  const openMin = sessionOpenMin(symbol);
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i].time);
    const ms = t < 1e12 ? t * 1000 : t;
    const ist = new Date(ms + IST_OFFSET_MS);
    const ymd = ist.toISOString().slice(0, 10);
    const today = new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
    if (ymd !== today) continue;
    const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
    if (mins === openMin) {
      idx = i;
      break;
    }
    if (idx < 0) idx = i;
    break;
  }
  if (idx < 0) return bars;
  const b = bars[idx];
  let o = Number(b.open);
  let h = Number(b.high);
  let l = Number(b.low);
  const c = Number(b.close);
  if (!(c > 0)) return bars;
  const glued = prevClose > 0 && o > 0 && Math.abs(o - prevClose) / prevClose < 0.0008;
  const off = o > 0 && Math.abs(o - dayOpen) / dayOpen > 0.0003;
  const gapPct =
    prevClose > 0 && dayOpen > 0 ? Math.abs(dayOpen - prevClose) / prevClose : 0;
  if (!glued && !off && gapPct < 0.0015 && !(dayLow > 0 && l > 0 && l < dayLow - 1e-6)) {
    return bars;
  }
  o = dayOpen;
  h = Math.max(o, c, h > 0 ? h : c);
  l = Math.min(o, c, l > 0 ? l : c);
  const dh = Number(dayHigh);
  const dl = Number(dayLow);
  // Cap wicks outside exchange day range only — never widen to day H/L (gap-fill candle).
  if (dh > 0 && h > dh) h = dh;
  if (dl > 0 && l < dl) l = dl;
  if (!(dh > 0) || !(dl > 0)) {
    const mcx = isMcxSymbol(symbol);
    const maxWick = mcx ? 0.04 : /NIFTY|SENSEX|BANK/i.test(symbol || "") ? 0.008 : 0.05;
    const hiCap = dayOpen * (1 + maxWick);
    const loCap = dayOpen * (1 - maxWick);
    if (!(dh > 0) && h > hiCap) h = Math.max(o, c);
    if (!(dl > 0) && l < loCap) l = Math.min(o, c);
  }
  const out = bars.slice();
  out[idx] = Object.assign({}, b, { open: o, high: h, low: l, close: c });
  return out;
}

export function samePriceRegime(a: BcBar[] | null | undefined, b: BcBar[] | null | undefined): boolean {
  if (!a || !a.length || !b || !b.length) return true;
  const ca = Number(a[a.length - 1].close);
  const cb = Number(b[b.length - 1].close);
  if (!(ca > 0) || !(cb > 0)) return true;
  const r = ca / cb;
  return r >= 0.82 && r <= 1.18;
}

export { resMinutes };
